
import { defineComponent } from 'vue'

import PaginationMixin from '~/mixins/paginationMixin'
import { resolveNodes } from '~/utils/array'

// Made a new query because, while editing the report, we need to know whether each repo
// has already been selected under a particular report.
import { publicReportSourceableRepositories } from '~/apollo/queries/reports/publicReportSourceableRepositories.gql'

import { GraphqlQueryResponse } from '~/types/apolloTypes'
import {
  PublicReportSourceableRepositoriesQueryVariables,
  SourceableRepository
} from '~/types/types'

export default defineComponent({
  name: 'SelectRepositoriesForReport',
  mixins: [PaginationMixin],
  props: {
    ownerLogin: {
      type: String,
      required: true
    },
    vcsProvider: {
      type: String,
      required: true
    },
    editMode: {
      type: Boolean,
      required: true
    },
    reportId: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      selectedRepos: [] as Array<string>,
      unSelectedRepos: [] as Array<string>,
      repoList: [] as Array<SourceableRepository>,
      repoListLoading: false,
      query: '',
      perPageCount: 20,
      scrollTopVisible: false
    }
  },
  async fetch() {
    try {
      this.repoListLoading = true

      const args: PublicReportSourceableRepositoriesQueryVariables = {
        vcsProvider: this.$providerMetaMap[this.vcsProvider].value,
        ownerLogin: this.ownerLogin,
        publicReportId: this.reportId,
        first: this.perPageCount,
        offset: this.queryOffset,
        q: this.query
      }

      const response = (await this.$fetchGraphqlData(
        publicReportSourceableRepositories,
        args,
        true
      )) as GraphqlQueryResponse

      this.totalCount = response.data.publicReportSourceableRepositories?.totalCount ?? 0

      const newRepoList = resolveNodes(response.data.publicReportSourceableRepositories)

      this.repoList = [...this.repoList, ...newRepoList]

      this.selectedRepos = Array.from(
        new Set([...this.selectedRepos, ...this.alreadySelectedRepos(newRepoList)])
      )
    } catch (e) {
      this.$logErrorAndToast(e as Error, 'Unable to fetch repository list. Please contact support.')
    } finally {
      this.repoListLoading = false
    }
  },
  methods: {
    /**
     * Scroll event handler for the repo list container.
     * Controls visibility of scroll-to-top button.
     *
     * @returns {void}
     */
    onScroll(e: Event): void {
      const { clientHeight, scrollHeight, scrollTop } = e.target as HTMLElement

      //  Calculation below has been done through trial and error
      // ! Update this anytime HTML markup of this component is updated.
      this.scrollTopVisible = scrollHeight - (clientHeight + scrollTop) >= 50 && scrollTop > 10
    },

    /**
     * Reset scroll for the repo list container
     *
     * @returns {void}
     */
    resetRepoListScroll(): void {
      const parentDiv = this.$refs.reportSelectRepositoryList as HTMLElement

      if (parentDiv) {
        parentDiv.scrollTo({
          top: 0,
          behavior: 'smooth'
        })
      }
    },

    /**
     * Trigger fetch of next list of repos
     *
     * @returns {void}
     */
    showMore(): void {
      this.updatePageNum(this.currentPage + 1)
    },

    /**
     * Reset repoList and page no before fetching search results
     *
     * @returns {void}
     */
    handleSearch(): void {
      this.repoList = []
      this.currentPage = 1
      this.$fetch()
    },

    /**
     * Handler method for checkbox selection/deselection event.
     * Emits add-repo and remove-repo accordingly.
     *
     * @param {Array<string>} newSelectedRepos
     * @returns {void}
     */
    handleCheckboxSelection(newSelectedRepos: Array<string>): void {
      // Check which repos that were already selected but are not present in selected repos list anymore
      const removedRepos = this.selectedRepos.filter((repoId) => !newSelectedRepos.includes(repoId))

      // Check which repos which were not already selected but now are present in selected repos list
      const addedRepos = newSelectedRepos.filter((repoId) => !this.selectedRepos.includes(repoId))

      if (removedRepos.length) {
        this.$emit('remove-repo', removedRepos)
      }

      if (addedRepos.length) {
        this.$emit('add-repo', addedRepos)

        // Removed just selected repo from unselected repos
        this.unSelectedRepos = this.unSelectedRepos.filter((repoId) => !addedRepos.includes(repoId))
      }

      this.selectedRepos = newSelectedRepos

      // Need to maintain state of un selected repos, so on refetching repoList context of un selected repos is not lost
      this.unSelectedRepos = [...this.unSelectedRepos, ...removedRepos]
    },

    /**
     * Method that returns repos with isSourced field as true.
     * Used to get list of repos that were already selected by user.
     *
     * @param {Array<SourceableRepository>} newRepoList
     * @returns {Array<string>}
     */
    alreadySelectedRepos(newRepoList: Array<SourceableRepository>): Array<string> {
      if (!this.editMode) return []

      const selectedRepos: Array<string> = []

      newRepoList.forEach(
        (repo) =>
          repo.isSourced && !this.unSelectedRepos.includes(repo.id) && selectedRepos.push(repo.id)
      )

      return selectedRepos
    }
  }
})
